<template>
  <div>
    <div v-if="detailsLayoutTemplate && !!content?.id"
         class="details-wrapper">
        <div v-if="noRightColumnAccess"
             class="single-column">
          <column-layout :column-info="detailsLayoutTemplate.leftColumn"/>
        </div>
        <div v-else
             class="double-column">
          <template v-for="(columnInfo, colName) in detailsLayoutTemplate"
                    :key="colName">
            <column-layout :column-info="columnInfo"/>
          </template>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from "vue"
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import useDetailPage from "./useDetailPage"

import ColumnLayout from "@/components/library/content/details/ColumnLayout.vue"

const store = useStore()
const route = useRoute()
const {
  content,
  isPart,
  showComments,
  showRelated,
  showSuggestions,
  showAttachments
} = useDetailPage()

const detailsLayoutTemplate =  computed(() => store.getters['detailPageLayouts/getDetailPageLayoutState'])
const noRightColumnAccess = computed(() => {
  return !showRelated.value
    && !showSuggestions.value
    && !showComments.value
    && !showAttachments.value
})

const isWidget = computed(() => store.getters['widgets/isWidget'])
const widgetScrollToWhereUsed = computed(() => store.getters['content/getWidgetScrollToWhereUsed'])

async function fetchLayoutTemplate () {
  if(!content.value.type) {
    return
  }

  await store.dispatch('detailPageLayouts/fetchDetailPageLayout',
    {type: content.value.type, id: content.value.id})
}

async function scrollToContent(): Promise<void> {
  if (route.fullPath.includes('related') && showRelated.value) {
    document.getElementById("Related")?.scrollIntoView({ block: 'center' })
  }
  if (route.fullPath.includes('attachment') && showAttachments.value) {
    document.getElementById("Attachments")?.scrollIntoView()
  }
  if (route.fullPath.includes('comments') && showComments.value) {
    document.getElementById("Comments")?.scrollIntoView()
  }
  if (route.fullPath.includes('infoWrapper')) {
    document.getElementById("InfoWrapper")?.scrollIntoView()
  }
  if (route.fullPath.includes('suggestions') && showSuggestions.value) {
    document.getElementById("Suggestions")?.scrollIntoView()
  }
  if ((route.fullPath.includes('whereused')) || (isWidget.value && widgetScrollToWhereUsed.value)) {
    document.getElementById("WhereUsed")?.scrollIntoView()
    if (isWidget.value) {
      await store.dispatch('content/setWidgetScrollToWhereUsed', false)
    }
  }
}
// using a watcher to detect when content is being evaluated as the content object is not always available on mount event
// setting a watcher on content or its properties never fires, but on a dependent computed property, it does
watch(isPart, async val => {
  if(detailsLayoutTemplate.value) return
  await fetchLayoutTemplate()
  await scrollToContent()
})
// @ts-ignore
onMounted(async () => {
  await fetchLayoutTemplate()
  await scrollToContent()
})

</script>

<style scoped lang="scss">
.details-wrapper {
  display: flex;
  flex-direction: row;

  .single-column, .double-column {
    width: 100%;
  }
}
</style>
